import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, FileText, Users, Repeat, Command } from 'lucide-react';
import { api } from '../lib/api';

const CommandPalette = ({ open, onClose }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ invoices: [], clients: [], recurring: [] });
  const [activeIdx, setActiveIdx] = useState(0);
  const navigate = useNavigate();
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 50);
      setQuery('');
      setResults({ invoices: [], clients: [], recurring: [] });
      setActiveIdx(0);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      if (query.trim().length === 0) {
        setResults({ invoices: [], clients: [], recurring: [] });
        return;
      }
      api.get('/search', { params: { q: query } }).then(r => setResults(r.data));
    }, 180);
    return () => clearTimeout(t);
  }, [query, open]);

  const flat = [
    ...results.invoices.map(r => ({ ...r, kind: 'invoice' })),
    ...results.clients.map(r => ({ ...r, kind: 'client' })),
    ...results.recurring.map(r => ({ ...r, kind: 'recurring' })),
  ];

  const go = (item) => {
    if (item.kind === 'invoice') navigate(`/invoices/${item.id}`);
    if (item.kind === 'client') navigate(`/clients/${item.id}`);
    if (item.kind === 'recurring') navigate('/recurring');
    onClose();
  };

  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, flat.length - 1)); }
      if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
      if (e.key === 'Enter' && flat[activeIdx]) { e.preventDefault(); go(flat[activeIdx]); }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, flat, activeIdx]);

  if (!open) return null;

  const ICONS = { invoice: FileText, client: Users, recurring: Repeat };
  const KIND_LABELS = { invoice: 'Invoice', client: 'Client', recurring: 'Recurring' };

  let idx = -1;
  const renderGroup = (label, items, kind) => {
    if (!items.length) return null;
    return (
      <div className="mb-4 last:mb-0">
        <p className="font-body text-[10px] uppercase tracking-[0.25em] text-text-secondary px-4 mb-1">{label}</p>
        {items.map(item => {
          idx++;
          const Icon = ICONS[kind];
          const active = idx === activeIdx;
          return (
            <button
              key={item.id}
              data-testid={`palette-result-${item.id}`}
              onClick={() => go({ ...item, kind })}
              onMouseEnter={() => setActiveIdx(idx)}
              className={`w-full text-left px-4 py-3 flex items-center transition-colors ${active ? 'bg-maroon text-white' : 'hover:bg-cream'}`}
            >
              <Icon className={`w-4 h-4 mr-3 flex-shrink-0 ${active ? 'text-white' : 'text-text-secondary'}`} strokeWidth={1.5} />
              <div className="flex-1 min-w-0">
                <p className={`font-body text-sm font-medium ${active ? 'text-white' : 'text-text-primary'}`}>{item.label}</p>
                {item.subtitle && <p className={`font-body text-xs truncate ${active ? 'text-white/80' : 'text-text-secondary'}`}>{item.subtitle}</p>}
              </div>
              <span className={`font-body text-[10px] uppercase tracking-widest ml-2 ${active ? 'text-white/70' : 'text-text-secondary'}`}>{KIND_LABELS[kind]}</span>
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div data-testid="command-palette" className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-start justify-center z-50 pt-24 px-4 fade-up" onClick={onClose}>
      <div className="bg-cream border border-border w-full max-w-xl shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center px-4 border-b border-border">
          <Search className="w-4 h-4 text-text-secondary" strokeWidth={1.5} />
          <input
            ref={inputRef}
            data-testid="palette-input"
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setActiveIdx(0); }}
            placeholder="Search invoices, clients, recurring..."
            className="flex-1 bg-transparent outline-none px-3 py-4 font-body text-base"
          />
          <span className="font-body text-[10px] uppercase tracking-widest text-text-secondary border border-border px-2 py-1 mono-num">ESC</span>
        </div>
        
        <div className="max-h-[60vh] overflow-y-auto py-3">
          {query.trim().length === 0 ? (
            <div className="px-4 py-10 text-center">
              <Command className="w-8 h-8 mx-auto text-text-secondary mb-3" strokeWidth={1.5} />
              <p className="font-body text-sm text-text-secondary mb-1">Quick access to everything</p>
              <p className="font-body text-xs text-text-secondary">Type to search · ↑↓ to navigate · ↵ to open</p>
            </div>
          ) : flat.length === 0 ? (
            <p className="px-4 py-10 text-center font-body text-sm text-text-secondary">No matches</p>
          ) : (
            <>
              {renderGroup('Invoices', results.invoices, 'invoice')}
              {renderGroup('Clients', results.clients, 'client')}
              {renderGroup('Recurring', results.recurring, 'recurring')}
            </>
          )}
        </div>
        
        <div className="border-t border-border px-4 py-2 flex justify-between font-body text-[10px] uppercase tracking-widest text-text-secondary">
          <span>Press <kbd className="px-1.5 py-0.5 border border-border mono-num">⌘K</kbd> to open</span>
          <span><kbd className="px-1.5 py-0.5 border border-border mono-num">↵</kbd> to select</span>
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
